import {z} from 'zod';
import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import {VerdocsSpinner} from '@verdocs/web-sdk-react';
import {IProfile, updateOrganizationContact, VerdocsEndpoint} from '@verdocs/js-sdk';
import {Button, TextInput} from '../Components';
import {Dialog} from './Dialog';

const formSchema = z.object({
  first_name: z.string({error: 'Please enter a first name'}),
  last_name: z.string({error: 'Please enter a last name'}),
  email: z.string({error: 'Please enter an email address'}).email(),
  phone: z.string().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

interface DialogProps {
  contact: IProfile;
  onClose?: () => void;
}

export const EditContactDialog: FC<DialogProps> = ({contact, onClose = () => {}}) => {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
    },
  });

  const onSubmit = async (data: FormValues) => {
    console.log('sub', data);
    setSubmitting(true);
    await updateOrganizationContact(VerdocsEndpoint.getDefault(), contact.id, data)
      .then(() => {
        toast.success('Updated contact.');
        setSubmitting(false);
        onClose();
      })
      .catch((e: any) => {
        console.log('Error updating contact', e);
        toast.error(e.response?.data?.error || 'Error updating contact, please try again later.');
        setSubmitting(false);
      });
  };

  return (
    <Dialog onCancel={onClose}>
      <div className="mb-1 text-lg font-medium text-[#33364b]">Edit contact</div>
      <div className="mb-4 text-[13px] leading-normal text-gray-500">{contact.email}</div>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <TextInput label="First name" name="first_name" required />
          <TextInput label="Last name" name="last_name" required />
          <TextInput label="Email address" name="email" required />
          <TextInput label="Phone number" name="phone" placeholder="Optional" />

          <div className="mt-5 flex flex-row items-center justify-end gap-2">
            {submitting && <VerdocsSpinner mode="dark" />}
            <Button type="button" size="small" variant="secondary" label="Cancel" onClick={onClose} disabled={submitting} />
            <Button size="small" label="Save" type="submit" disabled={submitting || !form.formState.isDirty} />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
